import { Component, OnInit, OnDestroy } from '@angular/core';

// ionic
import { PopoverController } from '@ionic/angular';

// other libraries
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";

// rxjs
import { Subject } from "rxjs";
import { debounceTime, take, takeUntil } from 'rxjs/operators';

// components
import { AddNoteComponent } from "../../components/add-note/add-note.component";

// services
import { EventService } from "../../services/event.service";
import { UserService } from "../../services/user.service";
import { ParticipantService } from "../../services/participant.service";
import { PlatformService } from "../../services/platform.service";

// models
import { Event } from "../../models/event";
import { User } from "../../models/user";
import { ParticipantTimeslot } from "../../models/participant-timeslot";
import { EventTimeSlots } from "../../models/event-timeslots";

// config
import { environment } from "../../../environments/environment";

// extends
import { BasePage } from "../base";

@Component({
    selector: 'app-timeslots',
    templateUrl: './timeslots.page.html',
    styleUrls: ['./timeslots.page.scss'],
})

export class TimeslotsPage extends BasePage implements OnInit, OnDestroy {
    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * user
     *
     * @type {User}
     */
    public user: User = new User;

    /**
     * active event for user
     *
     * @type Event
     */
    public event: Event = new Event;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = true;

    /**
     * Event timeslots
     *
     * @type {EventTimeSlots}
     */
    public eventTimeslots: EventTimeSlots;

    /**
     * The loading state
     *
     * @type {boolean}
     */
    public allowEdit: boolean = false;

    /**
     * Participant timeslots
     *
     * @type {ParticipantTimeslot}
     */
    public participantTimeslot: ParticipantTimeslot = new ParticipantTimeslot;

    /**
     * Array of available days for time slots
     *
     * @type {Date[]}
     */
    public days: any[] = [];

    /**
     * Array of daily offset against zero minutes for first hour
     *
     * @type {number[]}
     */
    public dailyOffset: number[] = [];

    /**
     * Timestamp with selected day in tabs
     *
     * @type {number}
     */
    public selectedDay: number = 0;

    /**
     * selected all slots
     *
     * @type {boolean}
     */
    public masterCheck: boolean = false;

    /**
     * array of hourly checkbox
     *
     * @type {}
     */
    public hourCheck = {};

    /**
     * Show warning about empty time slots
     *
     * @type {boolean}
     */
    public attendeeTimeslotWarning: boolean = false;

    public generateHourlyTimes = false;

    /**
     * which select type should be used.. alert/popover/action-sheet
     *
     * @type {string}
     */
    public selectType: string = 'popover';

    /**
     * constructor
     *
     * @param translate
     * @param eventService
     * @param userService
     * @param appointmentService
     */
    constructor(
        public translate: TranslateService,
        public eventService: EventService,
        public userService: UserService,
        public participantService: ParticipantService,
        public popoverController: PopoverController,
        public plt: PlatformService
    ) {
        super();

        if (this.plt.sizeSm) {
            this.selectType = 'action-sheet';
        }
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {
        this.userService.getCurrentUser()
            .pipe(
                takeUntil(this.ngUnsubscribe),
                debounceTime(0)
            ).subscribe((user) => {
                this.user = user;
                if (this.user && this.user.id) {
                    this.user.selected_participant.event = new Event(this.user.selected_participant.event, this.user);

                    this.refresh();
                }
            })
    }

    /**
     * refresh dashboard
     *
     * @return void
     */
    public refresh($event?) {
        if (this.user.selected_participant.event.use_timeslots) {
            // load participant timeslot stored setting
            this.participantService.getTimeslot(this.user.selected_participant.id).subscribe((data) => {

                // if this setting does not exits, we crate them from default event setting for timeslots
                if (!data['participanTimeslots'] || !data['participanTimeslots'].hasOwnProperty('id')) {
                    // load timeslots from event if needed
                    this.eventService.getEventTimeSetting(this.user.selected_participant.event.id).pipe(take(1)).subscribe((eventData) => {
                        // create and generate timeslots from event config
                        this.eventTimeslots = new EventTimeSlots(eventData.event);
                        // copy time slots from event which are default
                        this.participantTimeslot.loadFromEvent(this.eventTimeslots);

                        // show pop up if needed to inform user about empty setting for timeslots
                        if (!sessionStorage.getItem('attendeeTimeslotWarning' + this.user.selected_participant.event.id)) {
                            this.attendeeTimeslotWarning = true;
                        }

                        // final updates for data processing
                        this.finalizeParticipantTimeslots(data);

                        // hide loading
                        if ($event) {
                            $event.target.complete();
                        } else {
                            this.loading = false;
                        }
                    });
                } else {
                    // otherwise use saved setting for current user
                    this.participantTimeslot = new ParticipantTimeslot(data['participanTimeslots']);
                    // final updates for data processing
                    this.finalizeParticipantTimeslots(data);

                    // hide loading
                    if ($event) {
                        $event.target.complete();
                    } else {
                        this.loading = false;
                    }
                }

            },
                (error) => {
                    // TODO
                    this.showError(error);
                });
        }
        else {
            // redirect to normal appointments
            setTimeout(() => {
                this.router.navigate([this.plt.defaultLink + '/appointments']);
            }, 500);
        }
    }

    /**
     * final prepration of timeslots
     */
    private finalizeParticipantTimeslots(data) {
        // there is array with days -> day is timestamp
        this.days = this.participantTimeslot.getDays();

        // get daily offset for first time
        this.dailyOffset = this.participantTimeslot.getDayilyOffset();

        // check if we should shou hourly times
        this.generateHourlyTimes = 60 % this.participantTimeslot.timeslots['timeslot_length'] == 0
            && this.participantTimeslot.timeslots['timeslot_length'] < 60;

        // mark appointment in slots as blocked
        this.participantTimeslot.markAppointment(data['appointments']);

        // generate hour checkbox models
        Object.keys(this.participantTimeslot.timeslots['items']).forEach((key) => {
            this.participantTimeslot.timeslots['items'][key].forEach((slot) => {
                let hour = moment(slot.start).set({ minute: 0, second: 0, millisecond: 0 });
                this.hourCheck[hour.valueOf()] = false;
            })
        });
    }

    /**
     * Proccess timestlot checkbox selecting - set/unset global/hourly checkbox if needed
     *
     */
    public slotSelected(slot) {
        slot.selected = !slot.selected;

        this.allowEdit = false;
        this.masterCheck = true;
        Object.keys(this.hourCheck).forEach(hour => {
            this.hourCheck[hour] = true;
        });
        // check every slot
        Object.keys(this.participantTimeslot.timeslots['items']).forEach((key) => {
            this.participantTimeslot.timeslots['items'][key].forEach((slot) => {
                // if there is no appoinment - these are ignored
                if (!slot.appointment) {
                    if (slot.selected) {
                        // allow edit if there is some selected
                        this.allowEdit = true;
                    } else {
                        // if some is not selected disable master checkbox
                        this.masterCheck = false;
                    }
                }
                // check hour check
                let hour = moment(slot.start).set({ minute: 0, second: 0, millisecond: 0 });
                // disable hourly checkbox
                if (!slot.selected) {
                    this.hourCheck[hour.valueOf()] = false;
                }
            })
        });
    }

    /**
     * select all timeslots in day
     */
    public selectAllDay(day) {
        var time = day.getTime();
        var lastSlotTime = time;

        this.participantTimeslot.timeslots['items'][time].forEach((slot, index) => {
            slot.selected = day.selectAll;
            this.allowEdit = day.selectAll;
            lastSlotTime = slot.start;
        });

        Object.keys(this.hourCheck).forEach(hour => {
            if (hour > time && hour <= lastSlotTime) {
                this.hourCheck[hour] = day.selectAll;
            }
        });
    }

    /**
     * Change status for selected slots
     *
     */
    public changeStatus(status?, hideMessage?) {

        // reset checkboxes if we have changed status
        if (status) {
            this.allowEdit = false;
            this.masterCheck = false;
            Object.keys(this.hourCheck).forEach(hour => {
                this.hourCheck[hour] = false;
            });
        }
        // copy data from participant timeslot seeting to be able to edit them before saving
        let data = JSON.parse(JSON.stringify(this.participantTimeslot));
        Object.keys(this.participantTimeslot.timeslots['items']).forEach((key) => {
            this.participantTimeslot.timeslots['items'][key].forEach((slot, index) => {
                if (!slot.appointment) {
                    if (slot.selected && status) {
                        delete slot.selected;
                        data.timeslots['items'][key][index].status = slot.status = status;
                    }
                }
                // remove data which we dont need to be stored on backend side
                delete data.timeslots['items'][key][index].selected;
                delete data.timeslots['items'][key][index].edit;
                delete data.timeslots['items'][key][index].appointment;
                delete data.timeslots['items'][key][index].hasValidationErrors;
            })
        });

        this.days.forEach((day) => {
            day.selectAll = false;
        });

        // store results in database
        if (this.participantTimeslot.id) {
            this.participantService.updateTimeslot(this.user.selected_participant.id, data).subscribe((data) => {
                if (!hideMessage) {
                    this.overlayService.showSuccess(data.message);
                }
                this.loading = false;
            }, error => {
                this.overlayService.showError(error.error.message);
                this.loading = false;
            });
        } else {
            this.participantService.createTimeslot(this.user.selected_participant.id, data).subscribe((data) => {
                if (!hideMessage) {
                    this.overlayService.showSuccess(data.message);
                }
                this.loading = false;
            }, error => {
                this.overlayService.showError(error.error.message);
                this.loading = false;
            });
        }
    }

    /**
     * Select/unselect all slots, filtered by day also if filter is applied
     *
     */
    public masterCheckChanged() {
        this.allowEdit = this.masterCheck;
        // set properly hourly checkboxes
        // we dont need to check day, because, when changing day, all is reset to default
        Object.keys(this.hourCheck).forEach(hour => {
            this.hourCheck[hour] = this.masterCheck;
        })
        // set properly daily checkboxes
        Object.keys(this.participantTimeslot.timeslots['items']).forEach((key) => {
            this.participantTimeslot.timeslots['items'][key].forEach((slot, index) => {
                if (this.selectedDay == 0 || this.selectedDay == Number(key)) {
                    slot.selected = this.masterCheck;
                }
            })
        });
    }

    /**
     * Changing checkbox selection by hour checkbox
     *
     */
    public hourCheckChanged(time) {
        this.hourCheck[time.valueOf()] = !this.hourCheck[time.valueOf()];
        // NOTE[jg] - refactor? problem with event longer than month...
        this.allowEdit = this.hourCheck[time.valueOf()];
        Object.keys(this.participantTimeslot.timeslots['items']).forEach((key) => {
            this.participantTimeslot.timeslots['items'][key].forEach((slot, index) => {
                let slotTime = moment.utc(slot.start);
                let selectedHour = moment.utc(time);
                if (slotTime.hour() == selectedHour.hour()
                    && slotTime.date() == selectedHour.date()
                    && slotTime.month() == selectedHour.month()
                    && slotTime.year() == selectedHour.year()) {
                    slot.selected = this.hourCheck[time.valueOf()];
                } else {
                    // if there is another selected checkbox -> allow edit
                    if (slot.selected) {
                        this.allowEdit = true;
                    }
                }
            })
        });

        this.days.forEach((day) => {
            day.selectAll = false;
        });
    }

    /**
     * Filter view - show only selected day
     *
     */
    public changeDay(timestamp?) {
        this.selectedDay = timestamp;
        // reset checkboxes
        this.allowEdit = this.masterCheck = false;
        this.masterCheckChanged();
    }

    /**
     * Hide warning about empty participant timeslot setting
     *
     */
    public closeAttendeeTimeslotWarning(permanent?: boolean) {
        if (permanent) {
            // store this default setting to user
            this.changeStatus(null, true);
        } else {
            sessionStorage.setItem('attendeeTimeslotWarning' + this.user.selected_participant.event.id, '' + this.user.selected_participant.event.id);
        }
        this.attendeeTimeslotWarning = false;
    }

    /**
     * Show pop up with note for slot
     */
    public switchEdit(event, slot) {
        event.stopPropagation();
        slot.edit = !slot.edit;
    }

    /**
     * Redirect to detail of selected appointment in appoinment list
     *
     */
    public displayAppointmentList(id: number) {
        this.router.navigate([this.plt.defaultLink + '/appointments/' + id]);
        return false;
    }

    /**
     * open and process modal to add note
     */
    async presentAddNote(ev: any, slot) {

        const popover = await this.popoverController.create({
            component: AddNoteComponent,
            event: ev,
            componentProps: { slot: slot, note: slot.note },
            cssClass: 'edit-note'
            //translucent: true
        });

        popover.onWillDismiss().then((data) => {
            if (data.data && data.data.slot) {
                data.data.slot.selected = true;
                this.changeStatus();
                data.data.slot.selected = false;
            }
        });

        return await popover.present();
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
